import {
	AlertTriangle,
	Ban,
	CheckCircle2,
	Info,
	RefreshCw,
	Search,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { fetchJson } from "../api/rest";
import { PageHeader } from "../components/layout/PageHeader";

/**
 * What Google's CSS Center actually holds, per product.
 *
 * The page is built around one distinction: most issues Google reports come back
 * `servability: unaffected` — advice, not impact. A scan of 552,583 entries found
 * 156,014 issue rows but only 7,961 products carrying something Google acts on.
 * Ranking by raw issue volume ranks by noise, so impacting and advisory issues are
 * shown apart and never summed together.
 */

interface Summary {
	submitted: number;
	withImpact: number;
	policyBlocked: number;
	awaitingRemoval: number;
	lastChecked: string | null;
	serving: { status: string; products: number }[];
	impactingIssues: {
		issue: string;
		attribute: string | null;
		products: number;
	}[];
	advisoryIssues: {
		issue: string;
		attribute: string | null;
		products: number;
	}[];
	policyIssues: { issue: string; products: number; stillLive: number }[];
	expiring: { expiresOn: string; products: number }[];
}

interface StatusRow {
	productId: number;
	name: string;
	slug: string | null;
	gtin: string | null;
	servingStatus: string;
	topIssue: string | null;
	topIssueAttribute: string | null;
	hasServingImpact: boolean;
	issueCodes: string[];
	policyBlocked: boolean;
	policyIssue: string | null;
	removedFromCssAt: string | null;
	googleExpiration: string | null;
	statusCheckedAt: string | null;
}

interface ListResponse {
	items: StatusRow[];
	total: number;
	page: number;
	totalPages: number;
}

const nf = new Intl.NumberFormat("da-DK");

const servingBadge = (status: string) => {
	const styles: Record<string, string> = {
		approved: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
		pending: "bg-amber-50 text-amber-700 ring-amber-600/20",
		disapproved: "bg-rose-50 text-rose-700 ring-rose-600/20",
		unknown: "bg-slate-100 text-slate-600 ring-slate-500/20",
	};
	return styles[status] ?? styles.unknown;
};

const StatCard = ({
	label,
	value,
	hint,
	icon: Icon,
	tone,
}: {
	label: string;
	value: string;
	hint?: string;
	icon: typeof CheckCircle2;
	tone: "good" | "warn" | "bad" | "neutral";
}) => {
	const dots = {
		good: "bg-emerald-500",
		warn: "bg-amber-500",
		bad: "bg-red-500",
		neutral: "bg-zinc-300",
	};
	return (
		<div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
			<p className="flex items-center gap-2 font-medium text-[0.7rem] text-zinc-500 uppercase tracking-[0.14em]">
				<Icon className="h-3.5 w-3.5 text-zinc-400" />
				{label}
			</p>
			<p className="tnum mt-3 flex items-center gap-2 font-semibold text-[26px] text-ink leading-none">
				{value}
				<span className={`size-1.5 rounded-full ${dots[tone]}`} />
			</p>
			{hint && <p className="mt-2 text-xs text-zinc-400">{hint}</p>}
		</div>
	);
};

const ErrorBanner = ({ message }: { message: string }) => (
	<div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
		{message}
	</div>
);

const IssueTable = ({
	title,
	description,
	rows,
	onPick,
	emphasis,
}: {
	title: string;
	description: string;
	rows: { issue: string; attribute: string | null; products: number }[];
	onPick: (issue: string) => void;
	emphasis: boolean;
}) => (
	<div className="rounded-xl border border-slate-200 bg-white shadow-sm">
		<div className="border-b border-slate-100 px-5 py-4">
			<h2
				className={`text-sm font-semibold ${emphasis ? "text-slate-800" : "text-slate-500"}`}
			>
				{title}
			</h2>
			<p className="mt-0.5 text-xs text-slate-400">{description}</p>
		</div>
		<div className="max-h-96 overflow-y-auto">
			{rows.length === 0 && (
				<p className="px-5 py-6 text-sm text-slate-400">Nothing recorded.</p>
			)}
			{rows.map((r) => (
				<button
					key={`${r.issue}-${r.attribute ?? ""}`}
					type="button"
					onClick={() => onPick(r.issue)}
					className="flex w-full items-center justify-between gap-3 border-b border-slate-50 px-5 py-2.5 text-left last:border-0 hover:bg-slate-50"
				>
					<span className="min-w-0">
						<span className="block truncate text-sm text-slate-700">
							{r.issue}
						</span>
						{r.attribute && (
							<span className="text-xs text-slate-400">
								attribute: {r.attribute}
							</span>
						)}
					</span>
					<span
						className={`shrink-0 text-sm font-semibold tabular-nums ${emphasis ? "text-slate-800" : "text-slate-400"}`}
					>
						{nf.format(r.products)}
					</span>
				</button>
			))}
		</div>
	</div>
);

const CssStatus = () => {
	const [summary, setSummary] = useState<Summary | null>(null);
	const [list, setList] = useState<ListResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [summaryError, setSummaryError] = useState<string | null>(null);
	const [listError, setListError] = useState<string | null>(null);

	const [scope, setScope] = useState<"" | "impact" | "policy">("impact");
	const [servingStatus, setServingStatus] = useState("");
	const [issue, setIssue] = useState("");
	const [q, setQ] = useState("");
	const [page, setPage] = useState(1);

	const loadList = useCallback(async () => {
		const params = new URLSearchParams({ page: String(page), limit: "50" });
		if (scope) params.set("scope", scope);
		if (servingStatus) params.set("servingStatus", servingStatus);
		if (issue) params.set("issue", issue);
		if (q) params.set("q", q);
		setList(await fetchJson<ListResponse>(`/css-status?${params}`));
	}, [page, scope, servingStatus, issue, q]);

	// Settled independently on purpose. Under Promise.all a failing list query
	// rejected before the summary resolved, so a single broken endpoint left the
	// whole page blank instead of showing the half that worked.
	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		const summaryReq = fetchJson<Summary>("/css-status/summary")
			.then((s) => {
				if (cancelled) return;
				setSummary(s);
				setSummaryError(null);
			})
			.catch((e: Error) => {
				if (!cancelled) setSummaryError(e.message);
			});
		const listReq = loadList()
			.then(() => {
				if (!cancelled) setListError(null);
			})
			.catch((e: Error) => {
				if (cancelled) return;
				setList(null);
				setListError(e.message);
			});
		Promise.all([summaryReq, listReq]).finally(() => {
			if (!cancelled) setLoading(false);
		});
		return () => {
			cancelled = true;
		};
	}, [loadList]);

	const approved =
		summary?.serving.find((s) => s.status === "approved")?.products ?? 0;
	const approvedPct =
		summary && summary.submitted > 0
			? ((approved / summary.submitted) * 100).toFixed(1)
			: "0.0";
	const impactPct =
		summary && summary.submitted > 0
			? ((summary.withImpact / summary.submitted) * 100).toFixed(2)
			: "0.00";

	const pickIssue = (next: string) => {
		setIssue((current) => (current === next ? "" : next));
		setPage(1);
	};

	return (
		<div className="space-y-6">
			<PageHeader
				kicker="Menu · CSS status"
				title="CSS status."
				qualifier="What Google holds for us."
				description={
					<>
						<p>
							What Google's CSS Center holds for each product, read back by the{" "}
							<code className="rounded bg-zinc-100 px-1 py-0.5 text-xs">
								css_status
							</code>{" "}
							job.
						</p>
						{summary?.lastChecked && (
							<p className="mt-2 text-xs text-zinc-400">
								Last scan{" "}
								{new Date(summary.lastChecked).toLocaleString("da-DK")}
							</p>
						)}
					</>
				}
			/>

			{summaryError && <ErrorBanner message={summaryError} />}

			{summary && (
				<>
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
						<StatCard
							label="Submitted to CSS"
							value={nf.format(summary.submitted)}
							hint={`${approvedPct}% approved`}
							icon={CheckCircle2}
							tone="good"
						/>
						<StatCard
							label="Issues that affect serving"
							value={nf.format(summary.withImpact)}
							hint={`${impactPct}% of the feed — the only list worth acting on`}
							icon={AlertTriangle}
							tone={summary.withImpact > 0 ? "warn" : "good"}
						/>
						<StatCard
							label="Policy-blocked"
							value={nf.format(summary.policyBlocked)}
							hint="Withheld from every upload"
							icon={Ban}
							tone={summary.policyBlocked > 0 ? "bad" : "good"}
						/>
						<StatCard
							label="Awaiting removal"
							value={nf.format(summary.awaitingRemoval)}
							hint={
								summary.awaitingRemoval > 0
									? "Still live in the feed — run css_remove_blocked"
									: "Nothing left to remove"
							}
							icon={RefreshCw}
							tone={summary.awaitingRemoval > 0 ? "bad" : "good"}
						/>
					</div>

					<div className="grid gap-4 lg:grid-cols-2">
						<IssueTable
							title="Affects serving"
							description="Google acts on these. Click to filter the list below."
							rows={summary.impactingIssues}
							onPick={pickIssue}
							emphasis
						/>
						<IssueTable
							title="Advisory only"
							description="Reported as servability: unaffected. Large, and mostly noise."
							rows={summary.advisoryIssues}
							onPick={pickIssue}
							emphasis={false}
						/>
					</div>

					{summary.policyIssues.length > 0 && (
						<div className="rounded-xl border border-rose-200 bg-white shadow-sm">
							<div className="flex items-center gap-2 border-b border-rose-100 bg-rose-50/50 px-5 py-4">
								<Ban className="h-4 w-4 text-rose-600" />
								<h2 className="text-sm font-semibold text-slate-800">
									Policy violations
								</h2>
								<span className="text-xs text-slate-500">
									never re-submitted; deleted from the feed by
									css_remove_blocked
								</span>
							</div>
							<div className="overflow-x-auto">
								<table className="w-full text-sm">
									<thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
										<tr>
											<th className="px-5 py-2.5 font-medium">Violation</th>
											<th className="px-5 py-2.5 text-right font-medium">
												Products
											</th>
											<th className="px-5 py-2.5 text-right font-medium">
												Still live
											</th>
										</tr>
									</thead>
									<tbody>
										{summary.policyIssues.map((r) => (
											<tr key={r.issue} className="border-t border-slate-100">
												<td className="px-5 py-2.5 text-slate-700">
													{r.issue ?? "unknown"}
												</td>
												<td className="px-5 py-2.5 text-right tabular-nums text-slate-700">
													{nf.format(r.products)}
												</td>
												<td
													className={`px-5 py-2.5 text-right tabular-nums font-medium ${r.stillLive > 0 ? "text-rose-600" : "text-emerald-600"}`}
												>
													{nf.format(r.stillLive)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}
				</>
			)}

			{/* ── Product list ── */}
			<div className="rounded-xl border border-slate-200 bg-white shadow-sm">
				<div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-4">
					<div className="relative min-w-56 flex-1">
						<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
						<input
							value={q}
							onChange={(e) => {
								setQ(e.target.value);
								setPage(1);
							}}
							placeholder="Product name, GTIN or id"
							className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none"
						/>
					</div>
					<select
						value={scope}
						onChange={(e) => {
							setScope(e.target.value as typeof scope);
							setPage(1);
						}}
						className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
					>
						<option value="impact">Affects serving</option>
						<option value="policy">Policy-blocked</option>
						<option value="">Everything</option>
					</select>
					<select
						value={servingStatus}
						onChange={(e) => {
							setServingStatus(e.target.value);
							setPage(1);
						}}
						className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
					>
						<option value="">Any status</option>
						<option value="approved">Approved</option>
						<option value="pending">Pending</option>
						<option value="disapproved">Disapproved</option>
						<option value="unknown">Unknown</option>
					</select>
					{issue && (
						<button
							type="button"
							onClick={() => setIssue("")}
							className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600 hover:bg-slate-200"
						>
							issue: {issue} ✕
						</button>
					)}
				</div>

				<div className="overflow-x-auto">
					<table className="w-full text-sm">
						<thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
							<tr>
								<th className="px-5 py-2.5 font-medium">Product</th>
								<th className="px-5 py-2.5 font-medium">Status</th>
								<th className="px-5 py-2.5 font-medium">Top issue</th>
								<th className="px-5 py-2.5 font-medium">Attribute</th>
								<th className="px-5 py-2.5 font-medium">Expires</th>
							</tr>
						</thead>
						<tbody>
							{loading && !list && (
								<tr>
									<td
										colSpan={5}
										className="px-5 py-8 text-center text-slate-400"
									>
										Loading…
									</td>
								</tr>
							)}
							{!loading && listError && (
								<tr>
									<td colSpan={5} className="px-5 py-6">
										<ErrorBanner message={listError} />
									</td>
								</tr>
							)}
							{!loading && !listError && list?.items.length === 0 && (
								<tr>
									<td
										colSpan={5}
										className="px-5 py-8 text-center text-slate-400"
									>
										No products match.
									</td>
								</tr>
							)}
							{list?.items.map((r) => (
								<tr key={r.productId} className="border-t border-slate-100">
									<td className="px-5 py-3">
										<a
											href={`/products/${r.productId}`}
											className="font-medium text-slate-700 hover:text-primary"
										>
											{r.name}
										</a>
										<span className="mt-0.5 block text-xs text-slate-400">
											#{r.productId}
											{r.gtin ? ` · ${r.gtin}` : ""}
										</span>
									</td>
									<td className="px-5 py-3">
										<span
											className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${servingBadge(r.servingStatus)}`}
										>
											{r.servingStatus}
										</span>
										{r.policyBlocked && (
											<span className="ml-1.5 inline-flex rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700 ring-1 ring-inset ring-rose-600/20">
												blocked
											</span>
										)}
									</td>
									<td className="px-5 py-3 text-slate-600">
										<span className="flex items-center gap-1.5">
											{r.hasServingImpact ? (
												<AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
											) : (
												<Info className="h-3.5 w-3.5 shrink-0 text-slate-300" />
											)}
											{r.topIssue ?? "—"}
										</span>
										{r.issueCodes.length > 1 && (
											<span className="text-xs text-slate-400">
												+{r.issueCodes.length - 1} more
											</span>
										)}
									</td>
									<td className="px-5 py-3 text-slate-500">
										{r.topIssueAttribute ?? "—"}
									</td>
									<td className="px-5 py-3 text-slate-500 tabular-nums">
										{r.googleExpiration
											? new Date(r.googleExpiration).toLocaleDateString("da-DK")
											: "—"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{list && list.totalPages > 1 && (
					<div className="flex items-center justify-between border-t border-slate-100 px-5 py-3">
						<p className="text-sm text-slate-500">
							{nf.format(list.total)} product(s) · page {list.page} of{" "}
							{list.totalPages}
						</p>
						<div className="flex gap-2">
							<button
								type="button"
								disabled={list.page <= 1}
								onClick={() => setPage((p) => p - 1)}
								className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-40"
							>
								Previous
							</button>
							<button
								type="button"
								disabled={list.page >= list.totalPages}
								onClick={() => setPage((p) => p + 1)}
								className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-40"
							>
								Next
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default CssStatus;
